import { Component, OnInit, Input, ViewChild, TemplateRef } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';
import { ActivatedRoute } from '@angular/router';
import { MDCTabBar } from '@material/tabs';
import { Observable } from 'rxjs/Observable';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { EmailService } from './email.service';
import { PluginsService } from '../plugins/plugins.service';
import { TranslateService } from '@ngx-translate/core';
import { ISession } from '../../models/session';
import { IEmail } from '../../models/email';
import { ITag } from '../../models/tag';
import { IPlugin } from '../../models/plugin';
import { clone, contains, has, some, pipe, equals } from 'ramda/es';
import { ConfirmationModalComponent } from '../modals/confirmation/confirmation-modal.component';
import { EmailEditorComponent } from './editor/editor.component';
import { CustomizationService } from '../customization/customization.service';
import { first, map, flatMap, tap } from 'rxjs/operators';
import { SessionsService } from '../sessions/sessions.service';

@Component({
  templateUrl: './email.component.html',
  selector: 'email-list',
  styleUrls: ['./email.component.scss']
})
export class EmailComponent implements OnInit {
  @ViewChild('editor') editor: EmailEditorComponent;

  public session: ISession;
  public currentEmail: IEmail = {
    type: '',
    language: '',
    subject: '',
    fromName: '',
    content: '',
    schedule: '',
    hasSchedule: false,
    hasMailingList: false
  };
  public emails: Array<IEmail>; // The list of emails
  public completions = [];
  private activateByType = {
    alerts: ({ type: { sessionType }, plugins }) =>
      plugins.some(plugin => plugin.name === 'alerts' && plugin.isActivated) &&
      sessionType !== 'interactive',
    invitations: ({ type: { accountType } }) => accountType === 'invitation',
    confirmations: ({ type: { accountType } }) =>
      contains(accountType, ['registration', 'pre_registration']),
    accounts: ({ type: { accountType } }) => accountType === 'generic_login'
  };
  private activeTab: string;
  constructor(
    private route: ActivatedRoute,
    private pluginService: PluginsService,
    private emailService: EmailService,
    private translateService: TranslateService,
    private modalService: BsModalService,
    private customizationService: CustomizationService,
    private sessionsService: SessionsService
  ) {}

  private getEmailTags() {
    return forkJoin(
      ['link', 'login', 'password', 'encryptedurl', 'date', 'hour', 'livedescription'].map(key =>
        this.translateService.get(`sessions.details.emails.tags.${key}`)
      )
    ).pipe(
      map(([link, login, password, encryptedurl, date, hour, livedescription]) => {
        return [
          {
            name: 'link',
            description: link,
            type: 'text',
            value: link,
            tag: '(!--link--)'
          },
          {
            name: 'html.link',
            description: link,
            type: 'text',
            value: link,
            tag: '(!--ct:html.link--)'
          },
          {
            name: 'login',
            description: login,
            type: 'text',
            value: login,
            tag: '(!--login--)'
          },
          {
            name: 'password',
            description: password,
            type: 'text',
            value: password,
            tag: '(!--password--)'
          },
          {
            name: 'encryptedurl',
            description: encryptedurl,
            type: 'text',
            value: encryptedurl,
            tag: '(!--encryptedurl--)'
          },
          {
            name: 'date',
            description: date,
            type: 'text',
            value: date,
            tag: '(!--ct:webevent.global.date--)'
          },
          {
            name: 'hour',
            description: hour,
            type: 'text',
            value: hour,
            tag: '(!--ct:webevent.global.hour--)'
          },
          {
            name: 'livedescription',
            description: livedescription,
            type: 'text',
            value: livedescription,
            tag: '(!--ct:webevent.global.description.content--)'
          }
        ];
      })
    );
  }

  ngOnInit() {
    const parentData$ = this.route.parent.data;
    const onError = message => err => {
      console.log(err);
      toastr.error(this.translateService.instant(message));
    };
    const onResponses = ([emails, plugins]: [IEmail[], IPlugin[]]) => {
      const type = this.session.types;
      this.emails = emails.filter(email => this.activateByType[email.type]({ type, plugins }));
      this.emails = this.emails.filter(email => email.type !== 'accounts');
      this.activeTab = this.emails.length > 0 ? this.emails[0].type : undefined;
    };
    const onCompletions = (completions: any[]) => {
      this.getEmailTags().subscribe(emailTags => {
        // this.completions = emailTags, ...completions]; / <- uncomment to have all tags
        this.completions = emailTags;
      });
    };
    const getEmailsAndPlugins = (session: ISession) => {
      this.session = session;
      return forkJoin(
        this.emailService.getAll(this.session),
        this.pluginService.getAll(this.session)
      );
    };
    const getCompletions = () => {
      return this.customizationService.getTags(this.session);
    };

    const getEmailsAndPlugins$ = pipe(flatMap(getEmailsAndPlugins), tap(onResponses));
    const getCompletions$ = pipe(flatMap(getCompletions), tap(onCompletions));
    parentData$
      .pipe(
        first(),
        map(route => route.data),
        getEmailsAndPlugins$,
        getCompletions$
      )
      .subscribe(null, onError('action.failed'));

    this.translateService.onLangChange.pipe(getCompletions$).subscribe();
  }

  public clearMail(email: IEmail) {
    const modal = this.modalService.show(ConfirmationModalComponent);
    (<ConfirmationModalComponent>modal.content).showConfirmationModal(
      this.translateService.instant('sessions.events.delete.email')
    );

    const closeSource$ = (<ConfirmationModalComponent>modal.content).onClose;
    const clearMail = result => {
      if (result === true) {
        email.content = ' '; // must be space
        return this.emailService.saveEmail(this.session, email);
      }
    };
    closeSource$.pipe(first(), tap(clearMail)).subscribe(
      () => toastr.success(this.translateService.instant('action.success')),
      err => toastr.error(this.translateService.instant('action.failed'))
    );
  }

  public save({ email }) {
    return this.emailService.saveEmail(this.session, email).subscribe(
      () => {
        this.emails = this.emails.map(e => (e.type === email.type ? email : e));
        toastr.success(this.translateService.instant('action.success'));
        this.sessionsService.setComplete(this.session, 'emails');
      },
      err => toastr.error(this.translateService.instant('action.failed'))
    );
  }

  public saveMailingList({ type, mailingList }) {
    return this.emailService.saveMailingList(this.session, <IEmail>{ type }, mailingList).subscribe(
      () => {
        toastr.success(this.translateService.instant('action.success'));
        this.sessionsService.setComplete(this.session, 'emails');
      },
      err => toastr.error(this.translateService.instant('action.failed'))
    );
  }

  public removeMailingList({ type, mailingList }) {
    return this.emailService.removeMailingList(this.session, <IEmail>{ type }, mailingList).subscribe(
      () => toastr.success(this.translateService.instant('action.success')),
      err => toastr.error(this.translateService.instant('action.failed'))
    );
  }

  public sendTestMail({type, address}) {
    this.emailService.sendTestEmail(this.session, type, address).subscribe(
      () => toastr.success(this.translateService.instant('action.success')),
      err => toastr.error(this.translateService.instant('action.failed'))
    );
  }

  public sendOndemandMail({type, mailingList}) {
    this.emailService.sendOndemandEmail(this.session, type, mailingList).subscribe(
      () => toastr.success(this.translateService.instant('action.success')),
      err => toastr.error(this.translateService.instant('action.failed'))
    );
  }

  public sendNow({type}) {
    this.emailService.sendMail(this.session, type).subscribe(
      () => toastr.success(this.translateService.instant('action.success')),
      err => toastr.error(this.translateService.instant('action.failed'))
    );
  }

  public reSendNow({type}) {
    this.emailService.reSendMail(this.session, type).subscribe(
      () => toastr.success(this.translateService.instant('action.success')),
      err => toastr.error(this.translateService.instant('action.failed'))
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/email/email.component.ts